import {
  FeatureDefinition,
  FeatureDenyParams,
  FeatureKey,
} from '~/modules/feature-access/policy';

class FeatureAccessService {
  private features: Map<FeatureKey, FeatureDefinition> = new Map();
  private accessMap: Map<FeatureKey, boolean> = new Map();

  registerFeature(def: FeatureDefinition): void {
    this.features.set(def.key, def);
  }

  hasAccess(key: FeatureKey): boolean {
    if (!this.features.has(key)) {
      console.warn(
        `Feature "${key}" is not registered. Returning "true" for hasAccess.`,
      );
      return true;
    }

    return this.accessMap.get(key) ?? true;
  }

  updateAccess(key: FeatureKey, hasAccess: boolean): void {
    this.accessMap.set(key, hasAccess);
  }

  tryAccess<T extends (...args: any[]) => void | Promise<void>>(
    key: FeatureKey,
    callback: T,
    denyParams?: FeatureDenyParams,
  ): (...args: Parameters<T>) => Promise<void> {
    const feature = this.features.get(key);

    const isAllowed = this.accessMap.get(key);

    const executeCallback = async (...args: Parameters<T>): Promise<void> => {
      const result = callback(...args);
      return result instanceof Promise ? result : Promise.resolve(result);
    };

    const denyAccess = async (): Promise<void> => {
      feature?.onDeny?.(denyParams);
      return Promise.resolve();
    };

    return async (...args: Parameters<T>) => {
      if (!feature) {
        console.warn(
          `Feature "${key}" is not registered. Proceeding with callback.`,
        );
        return executeCallback(...args);
      }

      if (!isAllowed) {
        return denyAccess();
      }

      return executeCallback(...args);
    };
  }
}

export const featureAccessService = new FeatureAccessService();
